import React from 'react';
import { Info, Plus, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ACTION_TYPES } from './automationConstants';
import { labelOf, textOf, useFormConfig, useFormMeta } from './formMeta';

/**
 * 2026-09-24: parameter editor for the `send_form` action
 * (Docs/RELEASE_NOTES_2026-09-24_FORMS.md). What to ask is a pre-chat form
 * (Settings -> Pre-chat Forms) -- the whole form or some of its questions;
 * this panel picks it and says how hard to ask. Every default shown as a placeholder is the account's real
 * effective setting (GET /form-config), never a number typed here.
 *
 * The Completed / Declined paths are plain nested action lists (same wire
 * shape as a branch case). They run later -- when the contact answers --
 * outside the rule's own run, so `wait` (Gap 3) and a nested `send_form`
 * are not offered here (the backend rejects them too).
 */

// Not offered inside Completed / Declined -- see the note above.
const OUTCOME_EXCLUDED = new Set(['wait', 'branch', 'send_form']);

type NestedAction = { action_type: string; action_parameters?: Record<string, any> };

interface Props {
  params: Record<string, any>;
  onChange: (key: string, value: any) => void;
  triggerEvent?: string;
  idPrefix: string;
  // Approved WhatsApp templates (same list notify_agent's override uses).
  templates?: Array<{ id: number; name: string; language?: string }>;
  renderNestedParams: (
    actionType: string,
    params: Record<string, any>,
    onChange: (key: string, value: any) => void,
    idPrefix: string,
  ) => React.ReactNode;
}

const Hint: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Info className="h-3 w-3 cursor-help text-muted-foreground/70" />
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[260px] text-xs">
      {text}
    </TooltipContent>
  </Tooltip>
);

const numberOrUndefined = (v: string) => (v === '' ? undefined : Number(v));

const OutcomeList: React.FC<{
  title: string;
  hint: string;
  actions: NestedAction[];
  onChange: (next: NestedAction[]) => void;
  idPrefix: string;
  renderNestedParams: Props['renderNestedParams'];
}> = ({ title, hint, actions, onChange, idPrefix, renderNestedParams }) => {
  const types = ACTION_TYPES.filter((t) => !OUTCOME_EXCLUDED.has(t.value));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs">{title}</Label>
        <Hint text={hint} />
      </div>
      {actions.map((a, i) => (
        <div key={i} className="space-y-1.5 rounded-md border border-border/60 bg-muted/10 p-2">
          <div className="flex items-center gap-1">
            <Select
              value={a.action_type}
              onValueChange={(v) => onChange(actions.map((x, j) => (j === i ? { action_type: v, action_parameters: {} } : x)))}
            >
              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              type="button" variant="ghost" size="sm"
              className="h-7 w-7 shrink-0 p-0 text-muted-foreground hover:text-destructive"
              onClick={() => onChange(actions.filter((_, j) => j !== i))}
              aria-label="Remove action"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          {renderNestedParams(
            a.action_type,
            a.action_parameters || {},
            (key, value) =>
              onChange(actions.map((x, j) => (j === i ? { ...x, action_parameters: { ...(x.action_parameters || {}), [key]: value } } : x))),
            `${idPrefix}-${i}`,
          )}
        </div>
      ))}
      <Button
        type="button" variant="ghost" size="sm" className="h-6 gap-1 px-1.5 text-xs"
        onClick={() => onChange([...actions, { action_type: types[0]?.value || 'add_label', action_parameters: {} }])}
      >
        <Plus className="h-3 w-3" /> Add action
      </Button>
    </div>
  );
};

const SendFormParamsEditor: React.FC<Props> = ({ params, onChange, triggerEvent, idPrefix, templates, renderNestedParams }) => {
  const meta = useFormMeta();
  const cfg = useFormConfig();
  const source: string = params.source || 'form';
  const enforcement: string = params.enforcement || 'nudge';
  const settings = cfg?.effective_settings || meta?.default_settings || {};
  const nudgeDefaults = settings.nudge_policy || {};
  const nudgePolicy = params.nudge_policy || {};
  const liveForms = (cfg?.forms || []).filter((f) => !f.archived_at);
  // A rule may name its form by key (normal) or by id.
  const formRef = params.form_key ?? (params.form_id != null ? String(params.form_id) : '');
  const selected = liveForms.find((f) => f.key === formRef || String(f.id) === formRef);
  const fieldKeys: string[] = params.field_keys || [];
  const checkpoint = meta?.checkpoints.find((c) => c.value === triggerEvent);
  const canHold = !!checkpoint?.can_hold;
  const setNudge = (key: string, value: any) => {
    const next = { ...nudgePolicy, [key]: value };
    Object.keys(next).forEach((k) => next[k] === undefined && delete next[k]);
    onChange('nudge_policy', Object.keys(next).length ? next : undefined);
  };
  // One onChange per user action: the rule editor merges each call into
  // the params it rendered with. form_key wins over a stale form_id on the
  // backend, and questions that aren't in the new form are ignored there.
  const pickForm = (key: string) => onChange('form_key', key);
  const pickedHere = fieldKeys.filter((k) => (selected?.fields || []).some((f) => f.key === k));

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Form</Label>
        <Select value={selected?.key || formRef} onValueChange={pickForm}>
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Choose a form" /></SelectTrigger>
          <SelectContent>
            {liveForms.map((f) => (
              <SelectItem key={f.key} value={f.key}>
                {f.name} · {labelOf(meta?.form_kinds, f.kind)} · {f.fields.length} question{f.fields.length === 1 ? '' : 's'}
                {f.enabled ? '' : ' · off'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {cfg && liveForms.length === 0 && (
          <p className="text-xs text-muted-foreground">No forms yet. Create one under Settings → Pre-chat Forms.</p>
        )}
        {formRef && cfg && !selected && (
          <p className="text-xs text-destructive">"{formRef}" doesn't exist (or is archived). Pick a form above.</p>
        )}
        {selected && !selected.enabled && (
          <p className="text-xs text-amber-700 dark:text-amber-400">This form is switched off, so the rule won't ask anything until it's switched on.</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Ask</Label>
        <Select value={source} onValueChange={(v) => onChange('source', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="form">The whole form</SelectItem>
            <SelectItem value="fields">Only some of its questions</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {source === 'fields' && (
        <div className="space-y-1.5">
          <Label className="text-xs">Questions</Label>
          <div className="flex flex-wrap gap-1">
            {(selected?.fields || []).map((f) => (
              <Badge
                key={f.key}
                variant={fieldKeys.includes(f.key) ? 'default' : 'outline'}
                className="cursor-pointer text-[10px] font-normal"
                onClick={() =>
                  onChange('field_keys', fieldKeys.includes(f.key) ? fieldKeys.filter((k) => k !== f.key) : [...fieldKeys, f.key])
                }
              >
                {f.label}
              </Badge>
            ))}
            {!selected && <span className="text-xs text-muted-foreground">Choose a form first.</span>}
          </div>
          {selected && pickedHere.length === 0 && (
            <p className="text-xs text-destructive">Pick at least one question of this form.</p>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Name for "times asked" / repeat tracking</Label>
            <Input
              className="h-7 text-xs"
              value={params.tracking_key || ''}
              onChange={(e) => onChange('tracking_key', e.target.value || undefined)}
              placeholder="e.g. community_nudge"
            />
          </div>
        </div>
      )}

      <Separator />

      <div className="space-y-1.5">
        <div className="flex items-center gap-1.5">
          <Label className="text-xs">How hard</Label>
          <Hint text="Required pauses the step (e.g. the complaint intake) until the form is answered or declined -- only on a checkpoint trigger. If they decline, the step continues anyway and is flagged. A nudge never pauses anything." />
        </div>
        <Select value={enforcement} onValueChange={(v) => onChange('enforcement', v)}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {(meta?.enforcements || []).map((e) => (
              <SelectItem key={e.value} value={e.value}>{e.label} — {e.description}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {enforcement === 'required' && !canHold && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            This trigger isn't a checkpoint that can pause, so this rule will ask as a nudge. Use a checkpoint trigger
            (e.g. "{labelOf(meta?.checkpoints, 'before_companion_intake')}") to require answers.
          </p>
        )}
      </div>

      <div className="flex items-center justify-between rounded-md border border-border/60 px-2.5 py-1.5">
        <span className="text-xs font-medium">Skip questions already answered</span>
        <Switch checked={params.skip_known !== false} onCheckedChange={(v) => onChange('skip_known', v ? undefined : false)} />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Give up after (hours)</Label>
          <Input
            type="number" min={0} className="h-7 text-xs"
            value={params.timeout_hours ?? ''}
            onChange={(e) => onChange('timeout_hours', numberOrUndefined(e.target.value))}
            placeholder={`${settings.timeout_hours ?? ''} (default)`}
          />
        </div>
        {enforcement === 'required' ? (
          <div className="space-y-1">
            <Label className="text-xs">Re-asks per question</Label>
            <Input
              type="number" min={0} className="h-7 text-xs"
              value={params.reask_limit ?? ''}
              onChange={(e) => onChange('reask_limit', numberOrUndefined(e.target.value))}
              placeholder={`${settings.reask_limit ?? ''} (default)`}
            />
          </div>
        ) : (
          <div className="space-y-1">
            <Label className="text-xs">Placement</Label>
            <Select value={nudgePolicy.placement || ''} onValueChange={(v) => setNudge('placement', v || undefined)}>
              <SelectTrigger className="h-7 text-xs"><SelectValue placeholder={`${nudgeDefaults.placement ?? ''} (default)`} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="append">Append to the AI reply</SelectItem>
                <SelectItem value="separate">Own message</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {enforcement === 'nudge' && (
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Max times to nudge</Label>
            <Input
              type="number" min={0} className="h-7 text-xs"
              value={nudgePolicy.max_asks ?? ''}
              onChange={(e) => setNudge('max_asks', numberOrUndefined(e.target.value))}
              placeholder={`${nudgeDefaults.max_asks ?? ''} (default)`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hours between nudges</Label>
            <Input
              type="number" min={0} className="h-7 text-xs"
              value={nudgePolicy.cooldown_hours ?? ''}
              onChange={(e) => setNudge('cooldown_hours', numberOrUndefined(e.target.value))}
              placeholder={`${nudgeDefaults.cooldown_hours ?? ''} (default)`}
            />
          </div>
        </div>
      )}

      {triggerEvent && !checkpoint && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Label className="text-xs">If the 24-hour window is closed, send template</Label>
            <Hint text="WhatsApp only allows normal messages within 24 hours of the contact's last message. When it has closed (e.g. a check-in days later), this approved template is sent instead, and the first question follows as soon as they reply. Without one, the form is skipped for that contact." />
          </div>
          <Select
            value={params.window_template_id ? String(params.window_template_id) : '__none__'}
            onValueChange={(v) => onChange('window_template_id', v === '__none__' ? undefined : Number(v))}
          >
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Don't send (skip the form)</SelectItem>
              {(templates || []).map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>{t.name}{t.language ? ` (${t.language})` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label className="text-xs">Intro (optional, overrides the form's)</Label>
        <Textarea
          rows={2}
          value={textOf(params.intro_text)}
          onChange={(e) => onChange('intro_text', e.target.value ? { ...(params.intro_text || {}), en: e.target.value } : undefined)}
          placeholder="e.g. Before I log this, a few quick questions."
        />
      </div>

      <Separator />

      <OutcomeList
        title="When they finish"
        hint="Runs once every question is answered. To do something LATER (e.g. a check-in in 7 days), make a separate rule triggered by 'Form completed' and put the wait there."
        actions={params.on_completed || []}
        onChange={(next) => onChange('on_completed', next.length ? next : undefined)}
        idPrefix={`${idPrefix}-done`}
        renderNestedParams={renderNestedParams}
      />
      <OutcomeList
        title="When they decline or don't answer in time"
        hint="Runs on decline, cancel, time-out or too many invalid answers. A held step always continues anyway (flagged) -- this is for extra actions such as a label or notifying an agent."
        actions={params.on_declined || []}
        onChange={(next) => onChange('on_declined', next.length ? next : undefined)}
        idPrefix={`${idPrefix}-declined`}
        renderNestedParams={renderNestedParams}
      />
    </div>
  );
};

export default SendFormParamsEditor;
